import type {
  BaseServiceOptions,
  Pagination,
  SummaryStocks,
  TBaseCatalogs,
  TBaseWarehouse,
  TRequestCatalog,
} from '@/core/types'
import {
  moduleServiceHandlers,
  catalogServiceHandlers,
  type TInventories,
  serviceConfig,
} from '../../..'
import { defaultPagination, useNotification } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { TArticle } from '@/modules/purchase-orders'
import { useCatalogStore } from '@/core/store/catalogs'
import { TMill } from '@/modules/mills'
const { model, details } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, filters } = options
  const store = useCatalogStore()
  const { errorHttpNotification } = useNotification()

  const loadRecords = useQuery<TInventories, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords],
    queryFn: () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data.meta ?? defaultPagination)
      }
    },
    select: (data) => ({
      ...data,
      data: data.data.map((record) => ({
        ...record,
        quantity: Number(record.quantity ?? 0).toFixed(2),
      })),
    }),
  })

  const warehouseCatalog = useQuery<TBaseWarehouse[]>(
    [serviceConfig.catalogs.warehouse],
    () => catalogServiceHandlers.warehouseCatalog.loadCatalog({}),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.warehouseCatalog,
    },
  )

  const articlesCatalog = useQuery<TArticle[], AxiosError>({
    queryKey: [serviceConfig.catalogs.articles, filters?.articlesCatalog],
    queryFn: () =>
      catalogServiceHandlers.articlesCatalog.loadCatalog({
        dynamicFilters: filters?.articlesCatalog,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.articlesCatalog,
  })

  const requestCatalog = useMutation<
    TBaseCatalogs,
    AxiosError,
    TRequestCatalog
  >(
    (payload: Record<any, any>) =>
      catalogServiceHandlers.categoriesCatalog.requestCatalog(payload),
    {
      onSuccess: (payload) => {
        store.setCatalogs(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadSummary = useQuery<SummaryStocks, AxiosError>({
    queryKey: [details, currentPage, filters?.loadSummary],
    queryFn: () =>
      moduleServiceHandlers.loadSummary({
        dynamicFilters: filters?.loadSummary,
        page: currentPage,
      }),
    onError: (error) => errorHttpNotification(error),
    enabled: enableQuery?.loadSummary,
    refetchOnWindowFocus: false,
  })

  const millsCatalog = useQuery<TMill[], AxiosError>({
    queryKey: [serviceConfig.catalogs.mills, filters?.millsCatalog],
    queryFn: () =>
      catalogServiceHandlers.millsCatalog.loadCatalog({
        dynamicFilters: filters?.millsCatalog,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.millsCatalog,
  })

  return {
    loadRecords,
    loadSummary,
    warehouseCatalog,
    articlesCatalog,
    requestCatalog,
    millsCatalog,
  }
}
